import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Checkbox, FormControlLabel,
  CircularProgress, Alert, InputAdornment, IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { loginParticipant } from '../services/api';
import api from '../services/api';
import MediaPanel from '../components/MediaPanel';
import { FooterSection } from './eventPage/canvas/FooterSection';
import { PreviewCtx } from './eventPage/contexts/index';
import { useSite } from '../context/SiteContext';

// ─── Login Form ────────────────────────────────────────────────────────────────
const LoginForm = ({ event, onSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const login   = event?.login  || {};
  const primary = event?.branding?.primaryColor || '#1565C0';
  const method  = login.method || 'email'; // 'email' | 'employeeNumber' | 'magicLink'

  const [email,            setEmail]            = useState('');
  const [password,         setPassword]         = useState('');
  const [empNumber,        setEmpNumber]        = useState('');
  const [empStep,          setEmpStep]          = useState('number'); // 'number' | 'password'
  const [tokenCode,        setTokenCode]        = useState(searchParams.get('token') || '');
  const [remember,         setRemember]         = useState(false);
  const [showPw,           setShowPw]           = useState(false);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState(null);
  const [magicEmail,       setMagicEmail]       = useState('');
  const [magicLinkSent,    setMagicLinkSent]    = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  // Auto-submit when token arrives in URL (?token=xxx)
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken && method === 'magicLink') {
      doLogin({ method: 'token', token: urlToken, eventSlug: event?.slug });
    }
  // eslint-disable-next-line
  }, []);

  const sendMagicLink = async () => {
    if (!magicEmail) return;
    setMagicLinkLoading(true);
    setError(null);
    try {
      await api.post('/auth/participant/request-magic-link', {
        email: magicEmail,
        eventSlug: event?.slug,
      });
      setMagicLinkSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send link. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const doLogin = async (payload) => {
    setError(null);
    setLoading(true);
    try {
      const res = await loginParticipant(payload);
      const store = remember ? localStorage : sessionStorage;
      store.setItem('govirtual_token', res.data.token);
      store.setItem('govirtual_user',  JSON.stringify(res.data.user));
      onSuccess?.(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Phase 1 — try with employee number only; if server says password required show phase 2
  const handleEmpNext = async () => {
    if (!empNumber.trim()) return;
    setError(null);
    setLoading(true);
    try {
      const res = await loginParticipant({
        method: 'employeeNumber',
        employeeNumber: empNumber.trim(),
        eventSlug: event?.slug,
      });
      const store = remember ? localStorage : sessionStorage;
      store.setItem('govirtual_token', res.data.token);
      store.setItem('govirtual_user',  JSON.stringify(res.data.user));
      onSuccess?.(res.data);
    } catch (err) {
      const data = err.response?.data || {};
      if (data.requiresPassword) {
        // Participant has a password — show password field
        setEmpStep('password');
        setError(null);
      } else {
        setError(data.message || 'Employee number not found.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    const base = { eventSlug: event?.slug || undefined, org: event?.organization || undefined };
    if (method === 'email')          doLogin({ ...base, method: 'email', email, password });
    if (method === 'employeeNumber') doLogin({ ...base, method: 'employeeNumber', employeeNumber: empNumber, password: password || undefined });
    if (method === 'magicLink')      doLogin({ ...base, method: 'token', token: tokenCode });
  };

  const handleRegister = () => {
    if (!event?.ctaFormSlug) return;
    const path = event.urlMode === 'flat'
      ? `/${event.ctaFormSlug}`
      : `/${event.slug}/${event.ctaFormSlug}`;
    navigate(path);
  };

  const labelSx = {
    fontSize: 11, fontWeight: 700, letterSpacing: 1,
    textTransform: 'uppercase', color: 'text.secondary',
    display: 'block', mb: 0.75,
  };
  const fieldSx = { mb: 2.5 };

  const pwAdornment = (
    <InputAdornment position="end">
      <IconButton size="small" onClick={() => setShowPw(s => !s)} tabIndex={-1}>
        {showPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 5, md: 6 }, maxWidth: 460, width: '100%', mx: 'auto' }}>
      {/* Title */}
      <Typography variant="h4" sx={{ mb: 0.75, letterSpacing: -0.5 }}>
        {method === 'magicLink'
          ? (login.title || 'Check your email for the access link')
          : (login.title || 'Welcome Back')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
        {method === 'magicLink'
          ? (login.subtitle || 'Enter your registered email address below and we\'ll send you a secure one-time sign-in link. The link will expire in 30 minutes and can only be used once.')
          : (login.subtitle || 'Please enter your credentials to access the event.')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Email + password ─────────────────────────────────────────── */}
      {method === 'email' && (
        <>
          <Box sx={fieldSx}>
            <Typography component="label" sx={labelSx}>Email Address</Typography>
            <TextField fullWidth size="small" placeholder="name@example.com"
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
          </Box>
          <Box sx={fieldSx}>
            <Typography component="label" sx={labelSx}>Password</Typography>
            <TextField fullWidth size="small" placeholder="••••••••"
              type={showPw ? 'text' : 'password'} value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              InputProps={{ endAdornment: pwAdornment }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={remember} onChange={e => setRemember(e.target.checked)} />}
              label={<Typography variant="body2">Remember Me</Typography>}
            />
            <Typography variant="body2"
              sx={{ color: primary, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
              Forgot Password?
            </Typography>
          </Box>
        </>
      )}

      {/* ── Employee number — two-phase ──────────────────────────────── */}
      {method === 'employeeNumber' && (
        <>
          {/* Phase 1: employee number */}
          <Box sx={fieldSx}>
            <Typography component="label" sx={labelSx}>Employee Number</Typography>
            <TextField
              fullWidth size="small" placeholder="e.g. EMP-001"
              value={empNumber}
              onChange={e => { setEmpNumber(e.target.value); if (empStep === 'password') setEmpStep('number'); }}
              onKeyDown={e => e.key === 'Enter' && (empStep === 'number' ? handleEmpNext() : handleSubmit())}
              disabled={empStep === 'password'}
              InputProps={empStep === 'password' ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      variant="caption" sx={{ color: primary, cursor: 'pointer', fontWeight: 600, pr: 0.5 }}
                      onClick={() => { setEmpStep('number'); setPassword(''); setError(null); }}>
                      Change
                    </Typography>
                  </InputAdornment>
                ),
              } : undefined}
            />
          </Box>

          {/* Phase 2: password — only shown after a failed no-password attempt */}
          {empStep === 'password' && (
            <Box sx={fieldSx}>
              <Typography component="label" sx={labelSx}>Password</Typography>
              <TextField
                fullWidth size="small" placeholder="••••••••" autoFocus
                type={showPw ? 'text' : 'password'} value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                InputProps={{ endAdornment: pwAdornment }}
              />
            </Box>
          )}

          <Box sx={{ mb: 3 }} />
        </>
      )}

      {/* ── Magic link ───────────────────────────────────────────────── */}
      {method === 'magicLink' && !searchParams.get('token') && (
        magicLinkSent ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{ fontSize: 48, mb: 2 }}>📧</Box>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Check your inbox</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              We sent a sign-in link to <strong>{magicEmail}</strong>. Click the link in the email to access the event. The link expires in 30 minutes.
            </Typography>
            <Button variant="text" size="small" sx={{ color: primary }}
              onClick={() => { setMagicLinkSent(false); setError(null); }}>
              Use a different email
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={fieldSx}>
              <Typography component="label" sx={labelSx}>Email Address</Typography>
              <TextField fullWidth size="small" placeholder="name@example.com"
                type="email" value={magicEmail} onChange={e => setMagicEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMagicLink()} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              Enter your registered email and we'll send you a secure sign-in link.
            </Typography>
            <Button fullWidth variant="contained" onClick={sendMagicLink} disabled={magicLinkLoading || !magicEmail}
              sx={{ py: 1.25, borderRadius: 100, fontWeight: 700, bgcolor: primary, '&:hover': { bgcolor: primary, filter: 'brightness(0.9)' } }}>
              {magicLinkLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send Sign-In Link'}
            </Button>
          </>
        )
      )}

      {/* Auto-consuming token from URL — show spinner */}
      {method === 'magicLink' && searchParams.get('token') && loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress sx={{ color: primary }} />
          <Typography variant="body2" color="text.secondary">Signing you in…</Typography>
        </Box>
      )}

      {/* Sign in button — hidden for magic link (has its own button above) */}
      {method !== 'magicLink' && (
        <Button
          variant="contained" fullWidth size="large"
          onClick={method === 'employeeNumber' && empStep === 'number' ? handleEmpNext : handleSubmit}
          disabled={loading}
          sx={{
            bgcolor: primary, borderRadius: 100, py: 1.5,
            fontWeight: 700, fontSize: 15, mb: 3,
            boxShadow: `0 6px 20px ${primary}40`,
            '&:hover': { bgcolor: primary, filter: 'brightness(0.9)' },
          }}
        >
          {loading
            ? <CircularProgress size={22} sx={{ color: '#fff' }} />
            : (method === 'employeeNumber' && empStep === 'number' ? 'Next' : 'Sign In')}
        </Button>
      )}

      {/* Register link */}
      {event?.ctaFormSlug && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
          Don't have an account?{' '}
          <Box component="span"
            sx={{ color: primary, fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={handleRegister}
          >
            Register Now
          </Box>
        </Typography>
      )}
    </Box>
  );
};

// ─── Main LoginPage ────────────────────────────────────────────────────────────
const NOOP = () => {};

function useBreakpoint() {
  const get = () => window.innerWidth <= 480 ? 'mobile' : window.innerWidth <= 900 ? 'tablet' : 'desktop';
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const h = () => setBp(get());
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return bp;
}

const LoginPage = () => {
  const { event, organization, shellHeaderConfig, shellFooterConfig } = useSite();
  const navigate = useNavigate();
  const login    = event?.login    || {};
  const branding = event?.branding || {};
  const primary  = branding.primaryColor || '#1565C0';
  const bp = useBreakpoint();

  const layout = 'card-left';
  const isSplit = ['image-left','image-right','card-left','card-right'].includes(layout);

  const handleSuccess = async () => {
    // 1. Use loginRedirectHref if set on the event — configured in the page builder
    const redirectHref = event?.login?.loginRedirectHref;
    if (redirectHref) {
      navigate(redirectHref);
      return;
    }

    // 2. Try the default lobby
    if (event?._id) {
      try {
        const res = await import('../services/api').then(m => m.fetchPublicLobby(event._id));
        const lobbySlug = res.data?.data?.slug;
        if (lobbySlug) {
          const path = event.urlMode === 'flat'
            ? `/${lobbySlug}`
            : `/${event.slug}/${lobbySlug}`;
          navigate(path);
          return;
        }
      } catch {}
    }

    // 3. Fallback to event landing page
    navigate(event?.urlMode === 'flat' ? '/' : `/${event?.slug || ''}`);
  };

  const mediaPanelProps = {
    mediaUrl:       login.mediaUrl       || branding.mediaUrl  || "https://res.cloudinary.com/dasne3x79/image/upload/v1777854356/loginbg.png",
    mediaType:      login.mediaType      || 'image',
    overlayOpacity: login.mediaOverlayOpacity ?? 0.45,
    primaryColor:   primary,
    secondaryColor: branding.secondaryColor,
  };

  const heroText = (
    <Box sx={{ p: { xs: 3, md: 4 } }}>
      {login.portalLabel && (
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', mb: 1 }}>
          {login.portalLabel}
        </Typography>
      )}
      <Typography variant="h3" sx={{ color: '#fff', fontWeight: 900, lineHeight: 1.15, mb: 1.5, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
        Welcome to {event?.name || 'the Event'}!
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}>
        Access your personalized event schedule, network with exhibitors, and join the plenary sessions.
      </Typography>
    </Box>
  );

  const content = <LoginForm event={event} onSuccess={handleSuccess} />;

  const centeredContent = (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto', bgcolor: 'background.paper' }}>
      {content}
    </Box>
  );

  const renderLayout = () => {
    if (layout === 'image-left' || layout === 'image-right') {
      const imgFirst = layout === 'image-left';
      return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {imgFirst && <MediaPanel {...mediaPanelProps} contentPosition="bottom-left" sx={{ flex: '0 0 50%', minHeight: { xs: 280, md: '100%' } }}>{heroText}</MediaPanel>}
          {centeredContent}
          {!imgFirst && <MediaPanel {...mediaPanelProps} contentPosition="bottom-right" sx={{ flex: '0 0 50%', minHeight: { xs: 280, md: '100%' } }}>{heroText}</MediaPanel>}
        </Box>
      );
    }

    if (layout === 'card-left' || layout === 'card-right') {
      const imgFirst = layout === 'card-left';
      return (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, md: 5 }, bgcolor: 'background.default' }}>
          <Box sx={{
            display: 'flex', flexDirection: { xs: 'column', md: 'row' },
            width: '100%', maxWidth: 960,
            height: { md: '70vh' }, maxHeight: { md: 700 },
            borderRadius: 4, overflow: 'hidden',
            boxShadow: '0 8px 48px rgba(0,0,0,0.14)',
            border: '1px solid', borderColor: 'divider',
          }}>
            {imgFirst && <MediaPanel {...mediaPanelProps} contentPosition="bottom-left" sx={{ flex: '0 0 45%', borderRadius: 0 }}>{heroText}</MediaPanel>}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto', bgcolor: 'background.paper' }}>
              {content}
            </Box>
            {!imgFirst && <MediaPanel {...mediaPanelProps} contentPosition="bottom-right" sx={{ flex: '0 0 45%', borderRadius: 0 }}>{heroText}</MediaPanel>}
          </Box>
        </Box>
      );
    }

    if (layout === 'image-bg') {
      return (
        <MediaPanel {...mediaPanelProps} contentPosition="center" sx={{ flex: 1 }}>
          <Box sx={{ width: '100%', maxWidth: 440, mx: 'auto', bgcolor: 'background.paper', borderRadius: 4, boxShadow: '0 24px 64px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            {content}
          </Box>
        </MediaPanel>
      );
    }

    // image-top (default)
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <MediaPanel {...mediaPanelProps} contentPosition="center" sx={{ height: { xs: 220, md: 300 }, flexShrink: 0 }}>
          {heroText}
        </MediaPanel>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, px: 2, bgcolor: 'background.default' }}>
          <Box sx={{ width: '100%', maxWidth: 460, bgcolor: 'background.paper', borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            {content}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{
      height:    isSplit ? '100vh' : undefined,
      minHeight: isSplit ? undefined : '100vh',
      overflow:  isSplit ? 'hidden'  : undefined,
      bgcolor: 'background.default',
      display: 'flex', flexDirection: 'column',
      // Offset for the builder header rendered by the shell.
      pt: (() => {
        if (!shellHeaderConfig || Object.keys(shellHeaderConfig).length === 0) return 0;
        const isPill = (shellHeaderConfig.template || 'classic') === 'pill';
        if (isPill) {
          const pillTop = shellHeaderConfig.style?.pillTop ?? 16;
          return `${pillTop + 56 + 8}px`;
        }
        return `${shellHeaderConfig.style?.height || 72}px`;
      })(),
    }}>
      {renderLayout()}
      {!isSplit && Object.keys(shellFooterConfig).length > 0 && (
        <PreviewCtx.Provider value={true}>
          <FooterSection
            section={{ id: 'shell-footer', type: 'footer' }}
            config={shellFooterConfig}
            selected={false}
            onSelectFooter={NOOP}
            breakpoint={bp}
          />
        </PreviewCtx.Provider>
      )}
    </Box>
  );
};

export default LoginPage;